import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Module {
    id: string;
    code: string;
    name: string;
    description?: string;
    route?: string;
    icon?: string;
    order: number;
    menuGroup?: string;
    isActive: boolean;
    /** OPB-263: acceso por rol (admin/operator), no por asignación individual. */
    accessByRole?: boolean;
    createdAt: string;
    updatedAt?: string;
}

export interface CreateModuleRequest {
    code: string;
    name: string;
    description?: string;
    route?: string;
    icon?: string;
    order: number;
    menuGroup?: string;
    isActive?: boolean;
}

export interface UpdateModuleRequest {
    name?: string;
    description?: string;
    route?: string;
    icon?: string;
    order?: number;
    menuGroup?: string;
    isActive?: boolean;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
    timestamp: string;
}

@Injectable({
    providedIn: 'root',
})
export class ModuleService {
    private apiUrl = `${environment.apiUrl}/v1`;
    private modulesChangedSubject = new Subject<void>();
    public modulesChanged$ = this.modulesChangedSubject.asObservable();

    constructor(
        // eslint-disable-next-line no-unused-vars
        private http: HttpClient
    ) {}

    /**
     * Notifica que los módulos han cambiado
     */
    notifyModulesChanged(): void {
        this.modulesChangedSubject.next();
    }

    /**
     * Obtener todos los módulos.
     *
     * `includeIntegrationModules`: true SOLO para la pantalla admin de
     * /modules (editar/eliminar) — trae las filas "de vitrina" de Matriz
     * Tarifaria/Contratos/Precio Diésel con su id real de `modules`, sin
     * fusionar integration_modules. El sidebar llama sin este parámetro.
     */
    getModules(includeIntegrationModules = false): Observable<ApiResponse<Module[]>> {
        const url = `${this.apiUrl}/modules`;
        let params = new HttpParams();
        if (includeIntegrationModules) {
            params = params.set('includeIntegrationModules', 'true');
        }
        return this.http.get<ApiResponse<Module[]>>(url, {
            headers: this.getAuthHeaders(),
            params,
        });
    }

    /**
     * Obtener módulo por ID
     */
    getModuleById(id: string): Observable<ApiResponse<Module>> {
        const url = `${this.apiUrl}/modules/${id}`;
        return this.http.get<ApiResponse<Module>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Crear nuevo módulo
     */
    createModule(moduleData: CreateModuleRequest): Observable<ApiResponse<Module>> {
        const url = `${this.apiUrl}/modules`;
        return this.http.post<ApiResponse<Module>>(url, moduleData, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Actualizar módulo
     */
    updateModule(
        id: string,
        moduleData: UpdateModuleRequest
    ): Observable<ApiResponse<Module>> {
        const url = `${this.apiUrl}/modules/${id}`;
        return this.http.put<ApiResponse<Module>>(url, moduleData, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Eliminar módulo
     */
    deleteModule(id: string): Observable<ApiResponse<void>> {
        const url = `${this.apiUrl}/modules/${id}`;
        return this.http.delete<ApiResponse<void>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener token de autenticación del localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('access_token');
    }

    /**
     * Crear headers con token de autenticación
     */
    private getAuthHeaders(): HttpHeaders {
        const token = this.getAuthToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        });
    }
}

